import React, { ReactElement, useEffect, useState } from 'react'
import Radom, { Deposit as IDeposit } from '../../state/Radom'
import TimeText from '../../components/TimeText'
import { Container } from '../../components/Animations'
import PaymentMethodDisplay from '../../components/PaymentMethodDisplay'
import { getMethod } from '../../util/Managed'
import BreadCrumbs from '../../components/Breadcrumbs'
import { useParams, useSearchParams } from 'react-router-dom'
import { Section, SectionContent, SectionItem, SubTitle } from '../../components/PaymentDetails'
import CopyButton from '../../components/CopyButton'
import { RADOM_COLORS } from '../../util/Constants'
import Spinner from '../../components/Spinner'

const Deposit = (): ReactElement => {
  const params = useParams()
  const [searchParams] = useSearchParams()
  const source = searchParams.get('source')

  const [isLoading, setIsLoading] = useState(true)
  const [deposit, setDeposit] = useState<IDeposit>()

  useEffect(() => {
    if (!params.id) {
      return
    }

    setIsLoading(true)
    Radom.getDeposit(params.id)
      .then(s => setDeposit(s))
      .catch(e => console.error('Failed to look up deposit', e))
      .finally(() => setIsLoading(false))
  }, [])

  if (isLoading) {
    return <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 140, gap: 20 }}>
      <Spinner />
    </div>
  }

  if (!deposit) {
    return <></>
  }

  return <Container>
    <div style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', rowGap: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <BreadCrumbs items={
          source === 'transactions'
            ? [
              { item: 'Transactions', to: '/transactions' },
              { item: deposit.id, to: `/deposit/${deposit.id}?source=transactions` }
            ]
            : [
              { item: 'Deposits', to: '/deposits' },
              { item: deposit.id, to: '.' }
            ]} />
      </div>

      <Section>
        <SubTitle style={{ fontSize: 18 }}>
          Deposit details
        </SubTitle>

        <SectionContent>
          <SectionItem>
            <SubTitle>ID</SubTitle>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14 }}>
              <span>{deposit.id}</span>
              <CopyButton text={deposit.id} />
            </div>

          </SectionItem>

          {
            deposit.depositMethod.Fiat &&
            <SectionItem>
              <SubTitle>Fiat gross deposit</SubTitle>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 14 }}>

                <PaymentMethodDisplay
                  method={{ network: 'Fiat', token: deposit.depositMethod.Fiat.fiatCurrency }}
                  excludeName />
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 14 }}>
                  <span>{deposit.fiatGrossAmount}</span>
                  <span>{getMethod('Fiat', deposit.depositMethod.Fiat.fiatCurrency).ticker}</span>
                </div>
              </div>
            </SectionItem>
          }

          <SectionItem>
            <SubTitle>Gross deposit</SubTitle>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 14 }}>
              {
                deposit.depositMethod.Crypto && <>
                  <PaymentMethodDisplay
                    style={{ marginRight: 5 }}
                    method={{
                      network: deposit.depositMethod.Crypto.network,
                      token: deposit.depositMethod.Crypto.token
                    }}
                    excludeName
                    disableTooltip={false} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 14 }}>
                    <span>{deposit.grossAmount}</span>
                    <span>{getMethod(
                      deposit.depositMethod.Crypto.network,
                      deposit.depositMethod.Crypto.token).ticker}</span>
                  </div>
                </>
              }

              {
                deposit.depositMethod.Fiat && <>
                  <PaymentMethodDisplay
                    style={{ marginRight: 5 }}
                    method={{
                      network:
                      deposit.depositMethod.Fiat.onrampNetwork,
                      token: deposit.depositMethod.Fiat.onrampToken
                    }}
                    excludeName />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 14 }}>
                    <span>{deposit.grossAmount}</span>
                    <span>{getMethod(
                      deposit.depositMethod.Fiat.onrampNetwork,
                      deposit.depositMethod.Fiat.onrampToken).ticker}</span>
                  </div>
                </>
              }
            </div>
          </SectionItem>

          <SectionItem>
            <SubTitle>Net deposit</SubTitle>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 14 }}>
              {
                deposit.depositMethod.Crypto && <>
                  <PaymentMethodDisplay
                    style={{ marginRight: 5 }}
                    method={{
                      network: deposit.depositMethod.Crypto.network,
                      token: deposit.depositMethod.Crypto.token
                    }}
                    excludeName
                    disableTooltip={false} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 14 }}>
                    <span>{deposit.netAmount}</span>
                    <span>{getMethod(
                      deposit.depositMethod.Crypto.network,
                      deposit.depositMethod.Crypto.token).ticker}</span>
                  </div>
                </>
              }

              {
                deposit.depositMethod.Fiat && <>
                  <PaymentMethodDisplay
                    style={{ marginRight: 5 }}
                    method={{
                      network:
                      deposit.depositMethod.Fiat.onrampNetwork,
                      token: deposit.depositMethod.Fiat.onrampToken
                    }}
                    excludeName />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 14 }}>
                    <span>{deposit.netAmount}</span>
                    <span>{getMethod(
                      deposit.depositMethod.Fiat.onrampNetwork,
                      deposit.depositMethod.Fiat.onrampToken).ticker}</span>
                  </div>
                </>
              }
            </div>
          </SectionItem>

          <SectionItem>
            <SubTitle>Fee</SubTitle>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 14 }}>
              {
                deposit.depositMethod.Crypto && <>
                  <PaymentMethodDisplay
                    style={{ marginRight: 5 }}
                    method={{
                      network: deposit.depositMethod.Crypto.network,
                      token: deposit.depositMethod.Crypto.token
                    }}
                    excludeName
                    disableTooltip={false} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 14 }}>
                    <span>{deposit.fee}</span>
                    <span>{getMethod(
                      deposit.depositMethod.Crypto.network,
                      deposit.depositMethod.Crypto.token).ticker}</span>
                  </div>
                </>
              }

              {
                deposit.depositMethod.Fiat && <>
                  <PaymentMethodDisplay
                    style={{ marginRight: 5 }}
                    method={{
                      network:
                      deposit.depositMethod.Fiat.onrampNetwork,
                      token: deposit.depositMethod.Fiat.onrampToken
                    }}
                    excludeName />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 14 }}>
                    <span>{deposit.fee}</span>
                    <span>{getMethod(
                      deposit.depositMethod.Fiat.onrampNetwork,
                      deposit.depositMethod.Fiat.onrampToken).ticker}</span>
                  </div>
                </>
              }

            </div>
          </SectionItem>

          <SectionItem>
            <SubTitle>Deposited at</SubTitle>
            <div>
              <TimeText textColor={RADOM_COLORS.BLACK} date={new Date(deposit.depositedAt)} />
            </div>
          </SectionItem>
        </SectionContent>
      </Section>

    </div >
  </Container >
}

export default Deposit
